import { fileUrl } from "@/lib/api";
import {
  ShoppingCart, Download, ArrowRight, ExternalLink, Play, Check, Star,
  MessageCircle, Phone, Mail, Gift, Zap, Heart, Lock, CreditCard, Sparkles,
  Info, AlertTriangle, Shield, Bell, CheckCircle,
} from "lucide-react";

const TOKEN_RE = /([!@%]?)\[([^\]]*)\]\(([^)]*)\)/g;
const URL_RE = /(https?:\/\/[^\s<)]+)/g;

const BTN_SIZE_CLS = { sm: "px-3 py-1.5 text-xs", md: "px-5 py-2.5 text-sm", lg: "px-7 py-3.5 text-base" };
const BTN_SHAPE_CLS = { pill: "rounded-full", rounded: "rounded-xl", square: "rounded-none" };
const BTN_ICON_SIZE = { sm: 14, md: 16, lg: 18 };
export const BTN_ICONS = {
  cart: ShoppingCart, download: Download, arrow: ArrowRight, link: ExternalLink,
  play: Play, check: Check, star: Star, whatsapp: MessageCircle, phone: Phone,
  mail: Mail, gift: Gift, zap: Zap, heart: Heart, lock: Lock, card: CreditCard, sparkles: Sparkles,
};

const parseButtonOpts = (raw) => {
  const [url, ...opts] = raw.split("|");
  const cfg = {
    url: url.trim(), bg: "#0052FF", bg2: "#8B5CF6", fg: "#ffffff",
    size: "md", shape: "pill", anim: "none", d3: false, grad: false, icon: "", align: "left",
  };
  opts.forEach((opt) => {
    const eq = opt.indexOf("=");
    if (eq === -1) return;
    const key = opt.slice(0, eq).trim();
    const value = opt.slice(eq + 1).trim();
    if (!value) return;
    if (key === "bg") cfg.bg = value;
    if (key === "bg2") cfg.bg2 = value;
    if (key === "fg" || key === "color") cfg.fg = value;
    if (key === "size") cfg.size = value;
    if (key === "shape") cfg.shape = value;
    if (key === "anim") cfg.anim = value;
    if (key === "d3") cfg.d3 = value === "1" || value === "true";
    if (key === "grad") cfg.grad = value === "1" || value === "true";
    if (key === "icon") cfg.icon = value;
    if (key === "align") cfg.align = value;
  });
  return cfg;
};

const BTN_ALIGN_CLS = { left: "justify-start", center: "justify-center", right: "justify-end" };
const BTN_ONLY_RE = /@\[[^\]]*\]\([^)]+\)/g;

const isButtonOnlyLine = (line) => /@\[/.test(line) && line.replace(BTN_ONLY_RE, "").trim() === "";

const btnHref = (cfg) => {
  let href = cfg.url;
  if (href && !/^(https?:\/\/|\/|mailto:|tel:)/i.test(href)) href = `https://${href}`;
  return href;
};

function renderButtonRow(line, keyBase) {
  const btns = [];
  let m;
  let i = 0;
  let align = "left";
  BTN_ONLY_RE.lastIndex = 0;
  const re = /@\[([^\]]*)\]\(([^)]+)\)/g;
  while ((m = re.exec(line))) {
    const cfg = parseButtonOpts(m[2]);
    if (i === 0) align = cfg.align || "left";
    btns.push(
      <RichButton key={`${keyBase}-b${i++}`} href={btnHref(cfg)} cfg={cfg}>
        {m[1]}
      </RichButton>,
    );
  }
  return (
    <div key={keyBase} className={`flex flex-wrap items-center gap-1 ${BTN_ALIGN_CLS[align] || BTN_ALIGN_CLS.left}`}>
      {btns}
    </div>
  );
}

export const RichButton = ({ href, cfg, children }) => {
  const Icon = BTN_ICONS[cfg.icon];
  const cls = [
    "rb-btn",
    BTN_SIZE_CLS[cfg.size] || BTN_SIZE_CLS.md,
    BTN_SHAPE_CLS[cfg.shape] || BTN_SHAPE_CLS.pill,
    cfg.d3 ? "rb-3d" : "",
    cfg.grad ? "rb-grad" : "",
    cfg.anim && cfg.anim !== "none" ? `rb-anim-${cfg.anim}` : "",
  ].filter(Boolean).join(" ");
  const style = { color: cfg.fg };
  if (cfg.grad) style.backgroundImage = `linear-gradient(120deg, ${cfg.bg}, ${cfg.bg2}, ${cfg.bg})`;
  else style.backgroundColor = cfg.bg;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
      style={style}
      className={cls}
      data-testid="rich-button"
    >
      <span className="inline-flex items-center gap-2">
        {Icon && <Icon size={BTN_ICON_SIZE[cfg.size] || 16} strokeWidth={2.5} aria-hidden="true" />}
        {children}
      </span>
    </a>
  );
};

const parseOpts = (raw) => {
  const [url, ...opts] = raw.split("|");
  const style = {};
  opts.forEach((opt) => {
    const eq = opt.indexOf("=");
    if (eq === -1) return;
    const key = opt.slice(0, eq).trim();
    const value = opt.slice(eq + 1).trim();
    if (!value) return;
    if (key === "color") style.color = value;
    if (key === "size") style.fontSize = `${parseInt(value, 10)}px`;
    if (key === "font") style.fontFamily = value;
    if (key === "width") style.width = /^\d+$/.test(value) ? `${value}px` : value;
  });
  return { url: url.trim(), style };
};

// Style d'un texte enrichi (couleur / taille / police / gras) — token %[texte](|color=..|size=..|font=..|b=1)
const parseSpanStyle = (raw) => {
  const style = {};
  (raw || "").split("|").forEach((opt) => {
    const eq = opt.indexOf("=");
    if (eq === -1) return;
    const key = opt.slice(0, eq).trim();
    const value = opt.slice(eq + 1).trim();
    if (!value) return;
    if (key === "color") style.color = value;
    if (key === "size") style.fontSize = `${parseInt(value, 10)}px`;
    if (key === "font") style.fontFamily = value;
    if ((key === "b" || key === "bold") && (value === "1" || value === "true")) style.fontWeight = 800;
    if ((key === "i" || key === "italic") && (value === "1" || value === "true")) style.fontStyle = "italic";
    if ((key === "u" || key === "underline") && (value === "1" || value === "true")) style.textDecoration = "underline";
  });
  return style;
};

const Anchor = ({ href, style, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    style={style}
    onClick={(e) => e.stopPropagation()}
    className="font-bold text-brand underline underline-offset-2 transition-opacity duration-200 hover:opacity-75"
    data-testid="rich-link"
  >
    {children}
  </a>
);

function autolink(text, keyBase) {
  const parts = [];
  let last = 0;
  let i = 0;
  let match;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <Anchor key={`${keyBase}-u${i++}`} href={match[0]}>
        {match[0]}
      </Anchor>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderPlain(text, keyBase) {
  const parts = [];
  const BOLD_RE = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let i = 0;
  let match;
  BOLD_RE.lastIndex = 0;
  while ((match = BOLD_RE.exec(text))) {
    if (match.index > last) parts.push(...autolink(text.slice(last, match.index), `${keyBase}-p${i}`));
    parts.push(
      <strong key={`${keyBase}-b${i++}`} className="font-bold text-ink">
        {autolink(match[1], `${keyBase}-bi${i}`)}
      </strong>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(...autolink(text.slice(last), `${keyBase}-end`));
  return parts;
}

export function renderRichText(text) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let i = 0;
  let match;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(text))) {
    if (match.index > last) parts.push(...renderPlain(text.slice(last, match.index), `t${i}`));
    if (match[1] === "@") {
      const cfg = parseButtonOpts(match[3]);
      let href = cfg.url;
      if (href && !/^(https?:\/\/|\/|mailto:|tel:)/i.test(href)) href = `https://${href}`;
      parts.push(
        <RichButton key={`btn${i++}`} href={href} cfg={cfg}>
          {match[2]}
        </RichButton>,
      );
      last = match.index + match[0].length;
      continue;
    }
    if (match[1] === "%") {
      const style = parseSpanStyle(match[3]);
      parts.push(
        <span key={`s${i++}`} style={style} data-testid="rich-span">
          {renderPlain(match[2], `sp${i}`)}
        </span>,
      );
      last = match.index + match[0].length;
      continue;
    }
    const { url, style } = parseOpts(match[3]);
    if (match[1] === "!") {
      parts.push(
        <img
          key={`img${i++}`}
          src={fileUrl(url)}
          alt={match[2] || ""}
          style={style}
          loading="lazy"
          data-testid="rich-image"
          className="my-3 block h-auto max-w-full rounded-xl border border-line"
        />,
      );
    } else {
      parts.push(
        <Anchor key={`l${i++}`} href={url} style={style}>
          {match[2]}
        </Anchor>,
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(...renderPlain(text.slice(last), "end"));
  return parts;
}

// ------------------------------------------------------------------ alignement & cadres
const ALIGN_RE = /^\s*::(center|right|justify|left|c|r|j|l)\b[ \t]?/i;
const ALIGN_MAP = {
  c: "center", center: "center", r: "right", right: "right",
  j: "justify", justify: "justify", l: "left", left: "left",
};
const parseAlign = (line) => {
  const m = line.match(ALIGN_RE);
  if (!m) return { align: null, rest: line };
  return { align: ALIGN_MAP[m[1].toLowerCase()], rest: line.slice(m[0].length) };
};

const BOX_SHADOWS = {
  none: "",
  sm: "0 1px 2px rgba(0,0,0,0.06)",
  md: "0 6px 16px rgba(0,0,0,0.10)",
  lg: "0 14px 30px rgba(0,0,0,0.14)",
  xl: "0 24px 50px rgba(0,0,0,0.20)",
};

export const hexToRgba = (hex, opacity = 1) => {
  let h = String(hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return hex || "transparent";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const parseBoxOpts = (raw) => {
  const cfg = {
    border: "#e2e8f0", bg: "#ffffff", op: 100, bw: 1, radius: 16,
    shadow: "md", d3: false, glow: false, pad: 16, margin: 12, align: "left", icon: "", icolor: "",
  };
  (raw || "").split("|").forEach((opt) => {
    const eq = opt.indexOf("=");
    if (eq === -1) return;
    const k = opt.slice(0, eq).trim();
    const v = opt.slice(eq + 1).trim();
    if (!v) return;
    if (k === "border") cfg.border = v;
    else if (k === "bg") cfg.bg = v;
    else if (k === "op") cfg.op = Math.max(0, Math.min(100, parseInt(v, 10) || 0));
    else if (k === "bw") cfg.bw = Math.max(0, parseInt(v, 10) || 0);
    else if (k === "radius") cfg.radius = Math.max(0, parseInt(v, 10) || 0);
    else if (k === "shadow") cfg.shadow = v;
    else if (k === "d3") cfg.d3 = v === "1" || v === "true";
    else if (k === "glow") cfg.glow = v === "1" || v === "true";
    else if (k === "pad") cfg.pad = Math.max(0, parseInt(v, 10) || 0);
    else if (k === "margin") cfg.margin = Math.max(0, parseInt(v, 10) || 0);
    else if (k === "align") cfg.align = v;
    else if (k === "icon") cfg.icon = v;
    else if (k === "icolor") cfg.icolor = v;
  });
  return cfg;
};

export const computeBoxStyle = (cfg) => {
  const opacity = Math.max(0, Math.min(1, Number(cfg.op) / 100));
  const style = {
    borderStyle: "solid",
    borderWidth: `${cfg.bw}px`,
    borderColor: cfg.border,
    background: hexToRgba(cfg.bg, opacity),
    borderRadius: `${cfg.radius}px`,
    padding: `${cfg.pad}px`,
    margin: `${cfg.margin}px 0`,
    textAlign: cfg.align,
  };
  const shadows = [];
  if (cfg.d3) shadows.push("inset 0 1px 0 rgba(255,255,255,0.55)", "inset 0 -2px 8px rgba(0,0,0,0.06)");
  const drop = BOX_SHADOWS[cfg.shadow] || "";
  if (drop) shadows.push(drop);
  if (cfg.glow) shadows.push(`0 0 18px ${hexToRgba(cfg.border, 0.5)}`);
  if (shadows.length) style.boxShadow = shadows.join(", ");
  return style;
};

export const BOX_ICONS = {
  lock: Lock, info: Info, star: Star, warning: AlertTriangle, check: CheckCircle,
  shield: Shield, gift: Gift, zap: Zap, heart: Heart, bell: Bell, sparkles: Sparkles,
};
const BOX_ICON_JUSTIFY = { left: "flex-start", center: "center", right: "flex-end", justify: "flex-start" };

function renderBoxIcon(cfg, key) {
  const Icon = BOX_ICONS[cfg.icon];
  if (!Icon) return null;
  return (
    <div
      key={key}
      className="rt-box-icon"
      style={{ display: "flex", justifyContent: BOX_ICON_JUSTIFY[cfg.align] || "flex-start", marginBottom: 8 }}
    >
      <Icon size={30} strokeWidth={2.2} style={{ color: cfg.icolor || cfg.border }} aria-hidden="true" />
    </div>
  );
}

// Titre en dégradé : # {grad=#c1,#c2} Mon titre
const GRAD_RE = /^\{grad=\s*([^,}]+)\s*,\s*([^}]+?)\s*\}\s*/i;

function buildBlocks(lines, keyPrefix) {
  const blocks = [];
  let list = [];
  let btnBuf = [];
  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ul key={`${keyPrefix}-ul${blocks.length}`} className="rt-ul list-disc pl-5">
          {list.map((item, j) => (
            <li key={j}>{renderRichText(item)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };
  const flushBtns = () => {
    if (btnBuf.length) {
      blocks.push(renderButtonRow(btnBuf.join(" "), `${keyPrefix}-btnrow${blocks.length}`));
      btnBuf = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i];
    const trimmedStart = raw.replace(/^\s+/, "");

    // ---- Cadre (box) : :::box|opts ... :::
    if (trimmedStart.startsWith(":::box")) {
      flushList();
      flushBtns();
      const cfg = parseBoxOpts(trimmedStart.slice(6));
      const inner = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ":::") {
        inner.push(lines[i]);
        i += 1;
      }
      blocks.push(
        <div key={`${keyPrefix}-box${blocks.length}`} className="rt-box" data-testid="rich-box" style={computeBoxStyle(cfg)}>
          {renderBoxIcon(cfg, `${keyPrefix}-boxic${blocks.length}`)}
          {buildBlocks(inner, `${keyPrefix}-bx${blocks.length}`)}
        </div>,
      );
      continue;
    }
    if (raw.trim() === ":::") continue; // fermeture orpheline

    // ---- Ligne vide : espace (fidèle à la saisie)
    if (raw.trim() === "") {
      flushList();
      flushBtns();
      blocks.push(<div key={`${keyPrefix}-gap${blocks.length}`} className="rt-gap" aria-hidden="true" />);
      continue;
    }

    // ---- Alignement (::center / ::right / ::justify / ::left)
    const { align, rest } = parseAlign(raw);
    const restTrim = rest.replace(/^\s+/, "");
    const alignStyle = align ? { textAlign: align } : undefined;

    if (restTrim.startsWith("# ")) {
      flushList();
      flushBtns();
      let htext = restTrim.slice(2);
      const gm = htext.match(GRAD_RE);
      let headingInner;
      if (gm) {
        htext = htext.slice(gm[0].length);
        const gradStyle = {
          backgroundImage: `linear-gradient(90deg, ${gm[1].trim()}, ${gm[2].trim()})`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          WebkitTextFillColor: "transparent",
        };
        headingInner = <span className="rt-grad" style={gradStyle}>{renderRichText(htext)}</span>;
      } else {
        headingInner = renderRichText(htext);
      }
      blocks.push(
        <h4 key={`${keyPrefix}-h${blocks.length}`} className="rt-h4 font-extrabold text-ink" style={alignStyle}>
          {headingInner}
        </h4>,
      );
      continue;
    }
    if (restTrim.startsWith("- ")) {
      flushBtns();
      list.push(restTrim.slice(2));
      continue;
    }
    if (isButtonOnlyLine(restTrim)) {
      flushList();
      btnBuf.push(restTrim);
      continue;
    }
    flushList();
    flushBtns();
    blocks.push(
      <p key={`${keyPrefix}-p${blocks.length}`} className="rt-line" style={alignStyle}>
        {renderRichText(rest)}
      </p>,
    );
  }
  flushList();
  flushBtns();
  return blocks;
}

export function RichTextBlocks({ text, className = "" }) {
  if (!text) return null;
  const blocks = buildBlocks(String(text).split("\n"), "b");
  return <div className={`rt-content ${className}`}>{blocks}</div>;
}

export function stripRichText(text) {
  return (text || "")
    .replace(/^\s*:::box\b.*$/gm, "")
    .replace(/^\s*:::\s*$/gm, "")
    .replace(ALIGN_RE, "")
    .replace(/\{grad=[^}]*\}/g, "")
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    .replace(TOKEN_RE, "$2")
    .trim();
}

export default function RichText({ text, className }) {
  if (!text) return null;
  return <span className={className}>{renderRichText(text)}</span>;
}
